import { useCallback, useEffect, useState } from "react";
import type { PropsWithChildren, ReactNode } from "react";
import { MsalAuthenticationTemplate, MsalProvider } from "@azure/msal-react";
import { EventType, InteractionType, PublicClientApplication } from "@azure/msal-browser";
import type { IPublicClientApplication } from "@azure/msal-browser";
import SecuUserProvider from "./SecuUserProvider";
import { authenticationConfig } from "app-config";
import { initialiseAuthConfig } from "@sdeapps/react-core";
import storageService from "services/storageService";

let msalInstance: IPublicClientApplication | undefined;

interface SecuAuthentificationProviderProps extends PropsWithChildren {
  unauthorizedPage?: ReactNode;
}

/**
 * Config needed for :
 * - alternative local & session storage clear
 * - alternative retrieval of user (ex: offline Eau-secours utilise localStorage)
 * - ConnectionHandler connection redirect retry additional condition (ex: EauSecours isOnline)
 */
function SecuAuthentificationProvider({
  unauthorizedPage,
  children,
}: Readonly<SecuAuthentificationProviderProps>): ReactNode {
  const [msalInstanceState, setMsalInstanceState] = useState<IPublicClientApplication>();

  useEffect(() => {
    void (async () => {
      initialiseAuthConfig(authenticationConfig);
      msalInstance = new PublicClientApplication(authenticationConfig.msal);
      await msalInstance.initialize();
      setMsalInstanceState(msalInstance);

      msalInstance.addEventCallback((event) => {
        if (event.eventType === EventType.LOGOUT_SUCCESS) {
          // Au logout, on supprime du localStorage les infos de l'utilisateur
          storageService.removeUserInfo();
        }
      });
    })();
  }, []);

  const unauthorizedPageCallback = useCallback(() => <>{unauthorizedPage}</>, [unauthorizedPage]);

  if (msalInstanceState == null) {
    return unauthorizedPage;
  } else {
    return (
      // Provide l'instance de MSAL pour ses enfants avec le hook `useMsal()`
      <MsalProvider instance={msalInstanceState}>
        {/* Lance l'authentification si nécessaire et render le composant pertinent */}
        <MsalAuthenticationTemplate
          // interactionType={authenticationConfig.interactionType ?? InteractionType.Silent}
          interactionType={InteractionType.Redirect}
          authenticationRequest={authenticationConfig.msal.auth}
          loadingComponent={unauthorizedPageCallback}
          errorComponent={unauthorizedPageCallback}>
          {/* Provide l'utilisateur et ses roles une fois trouvés. Tant qu'on n'a pas trouvé l'utilisateur,
              affiche unauthorizedPage. Ne render les enfants QUE lorsque l'utilisateur est trouvé */}
          <SecuUserProvider unauthorizedPage={unauthorizedPage}>{children}</SecuUserProvider>
        </MsalAuthenticationTemplate>
      </MsalProvider>
    );
  }
}

// eslint-disable-next-line react-refresh/only-export-components
export { SecuAuthentificationProvider, msalInstance };
